import { z } from 'zod';

// zod v4 schemas passed directly to a route's `validateSearch`. Per-field
// `.catch()` falls a malformed/absent param back to `undefined` instead of
// throwing, so a bad link still renders the page and its own error state.

/** /login — post-login redirect target + access/expired banners. */
export const loginSearchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
  expired: z.boolean().optional().catch(undefined),
  access: z.string().optional().catch(undefined),
});

/** /view — the shared-link sign-in gate. Carries the attempted campaign URL so
 *  the gate can forward it to /login and land there after sign-in. */
export const viewSearchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
});

/** /reset-password — Firebase password-reset oobCode (also navigated to from /auth/action). */
export const resetSearchSchema = z.object({
  oobCode: z.string().optional().catch(undefined),
  token: z.string().optional().catch(undefined),
});

/** /invite + /activate — single-use set-password token. */
export const inviteSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

/** /auth/action — Firebase email-action link (mode=verifyEmail|resetPassword). */
export const authActionSearchSchema = z.object({
  mode: z.string().optional().catch(undefined),
  oobCode: z.string().optional().catch(undefined),
});

/** /waitlist/verify — brand waitlist email-verification link. */
export const waitlistVerifySearchSchema = z.object({
  email: z.string().optional().catch(undefined),
  token: z.string().optional().catch(undefined),
});

/** /login/code — OTP login; email+link_token present when opened from the emailed link. */
export const otpLoginSearchSchema = z.object({
  email: z.string().optional().catch(undefined),
  link_token: z.string().optional().catch(undefined),
  redirect: z.string().optional().catch(undefined),
});

/** /join/$brandSlug — self-service domain-join request; email pre-fills from a shared link. */
export const joinSearchSchema = z.object({
  email: z.string().optional().catch(undefined),
});

export type LoginSearch = z.infer<typeof loginSearchSchema>;
export type ResetSearch = z.infer<typeof resetSearchSchema>;
export type InviteSearch = z.infer<typeof inviteSearchSchema>;
export type AuthActionSearch = z.infer<typeof authActionSearchSchema>;
export type WaitlistVerifySearch = z.infer<typeof waitlistVerifySearchSchema>;
export type OtpLoginSearch = z.infer<typeof otpLoginSearchSchema>;
export type JoinSearch = z.infer<typeof joinSearchSchema>;
